import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Layout from '@/components/Layout';
import { useAuth } from '@/contexts/AuthContext';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, Award, BookOpen, TrendingUp, BookCheck, Calendar, KeyRound, Eye, EyeOff, Check } from 'lucide-react';
import ImageUpload from '@/components/ImageUpload';
import { toast } from 'sonner';
import { formatHalaman } from '@/utils/halaman';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const KELAS_CONFIG = {
  D: { label: "I'dad", color: 'bg-gray-100 text-gray-700' },
  C: { label: 'Uula', color: 'bg-yellow-100 text-yellow-700' },
  B: { label: 'Wustha', color: 'bg-blue-100 text-blue-700' },
  A: { label: 'Ulya', color: 'bg-emerald-100 text-emerald-700' },
  M: { label: 'Ali/Mahir', color: 'bg-purple-100 text-purple-700' },
};

function calcStreak(setoran) {
  if (!setoran.length) return 0;
  const dates = [...new Set(setoran.map(s => s.tanggal).filter(Boolean))].sort().reverse();
  let streak = 0;
  let ref = new Date();
  ref.setHours(0,0,0,0);
  for (const d of dates) {
    const dt = new Date(d);
    dt.setHours(0,0,0,0);
    const diff = Math.round((ref - dt) / 86400000);
    if (diff <= 1) { streak++; ref = dt; }
    else break;
  }
  return streak;
}

function calcBest(setoran) {
  if (!setoran.length) return '—';
  const gradeOrder = { A: 4, B: 3, C: 2, D: 1 };
  const best = setoran.reduce((best, s) => {
    const g = s.nilai_huruf || s.grade;
    return (gradeOrder[g] || 0) > (gradeOrder[best] || 0) ? g : best;
  }, 'D');
  return best;
}

export default function SantriProfile() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [santriData, setSantriData] = useState(null);
  const [setoran, setSetoran] = useState([]);
  const [progress, setProgress] = useState(null);
  const [halaqahList, setHalaqahList] = useState([]);
  const [editFoto, setEditFoto] = useState(false);
  const [newFoto, setNewFoto] = useState('');
  const [saving, setSaving] = useState(false);

  // password state
  const [showPassForm, setShowPassForm] = useState(false);
  const [passForm, setPassForm] = useState({ lama: '', baru: '', konfirmasi: '' });
  const [showPass, setShowPass] = useState({ lama: false, baru: false });
  const [savingPass, setSavingPass] = useState(false);

  useEffect(() => {
    axios.get(`${API}/halaqah`).then(r => setHalaqahList(r.data)).catch(() => {});
    if (user?.santri_id) {
      Promise.all([
        axios.get(`${API}/santri/${user.santri_id}`).then(r => setSantriData(r.data)).catch(() => {}),
        axios.get(`${API}/setoran?santri_id=${user.santri_id}`).then(r => setSetoran(r.data)).catch(() => {}),
        axios.get(`${API}/santri/${user.santri_id}/progress`).then(r => setProgress(r.data)).catch(() => {}),
      ]);
    }
  }, [user]);

  const halaqahName = halaqahList.find(h => h.id === santriData?.halaqah_id)?.nama || '—';
  const streak = calcStreak(setoran);
  const bestGrade = calcBest(setoran);
  const totalHalaman = formatHalaman(progress?.total_pages || 0);
  const totalSetoran = progress?.total_setoran || setoran.length;

  const saveFoto = async () => {
    if (!newFoto || !user?.santri_id) return;
    setSaving(true);
    try {
      await axios.put(`${API}/santri/me/foto`, { foto: newFoto });
      setSantriData(prev => ({ ...prev, foto: newFoto }));
      setEditFoto(false);
      toast.success('Foto profil berhasil diperbarui');
    } catch { toast.error('Gagal memperbarui foto'); }
    finally { setSaving(false); }
  };

  const savePassword = async (e) => {
    e.preventDefault();
    if (passForm.baru !== passForm.konfirmasi) {
      toast.error('Konfirmasi password tidak cocok'); return;
    }
    if (passForm.baru.length < 6) {
      toast.error('Password baru minimal 6 karakter'); return;
    }
    setSavingPass(true);
    try {
      await axios.post(`${API}/auth/change-password`, {
        password_lama: passForm.lama,
        password_baru: passForm.baru,
      });
      toast.success('Password berhasil diubah!');
      setShowPassForm(false);
      setPassForm({ lama: '', baru: '', konfirmasi: '' });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Gagal mengubah password');
    } finally { setSavingPass(false); }
  };

  const stats = [
    { icon: BookCheck, label: 'Total Setoran', value: totalSetoran, color: 'emerald' },
    { icon: BookOpen, label: 'Total Halaman', value: totalHalaman, color: 'blue' },
    { icon: Award, label: 'Nilai Terbaik', value: bestGrade, color: 'amber' },
    { icon: TrendingUp, label: 'Streak Hari', value: streak, color: 'purple' },
  ];

  const infoItems = [
    { label: 'NIS', value: santriData?.nis },
    { label: 'Kelas', value: santriData?.kelas ? `${santriData.kelas} - ${KELAS_CONFIG[santriData.kelas]?.label}` : null },
    { label: 'Halaqah', value: halaqahName },
    { label: 'Jenis Kelamin', value: santriData?.jenis_kelamin },
    { label: 'Tahun Masuk', value: santriData?.tahun_masuk },
    { label: 'Tanggal Lahir', value: santriData?.tanggal_lahir },
    { label: 'Alamat', value: santriData?.alamat },
  ].filter(i => i.value);

  return (
    <Layout>
      <div className="min-h-screen bg-gray-50">
        {/* Header */}
        <div className="bg-gradient-to-br from-emerald-950 via-emerald-800 to-emerald-700 px-5 pt-12 pb-10 relative overflow-hidden">
          <div className="absolute -top-8 -right-8 w-36 h-36 bg-white/5 rounded-full pointer-events-none" />
          <button onClick={() => navigate('/santri')}
            className="flex items-center gap-1.5 text-emerald-300 text-sm mb-5 hover:text-white transition-colors">
            <ChevronLeft size={16} /> Kembali
          </button>

          {/* Photo - centered */}
          <div className="flex flex-col items-center gap-3">
            <div className="relative">
              <div className="w-24 h-24 rounded-full ring-[3px] ring-amber-400 ring-offset-2 ring-offset-emerald-900 overflow-hidden bg-emerald-700 flex items-center justify-center shadow-2xl">
                {(editFoto ? newFoto : santriData?.foto)
                  ? <img src={editFoto ? newFoto : santriData?.foto} alt={user?.name} className="w-full h-full object-cover" />
                  : <span className="text-white font-bold text-3xl">{user?.name?.charAt(0)}</span>}
              </div>
              {!editFoto && (
                <button onClick={() => setEditFoto(true)}
                  data-testid="change-foto-btn"
                  className="absolute -bottom-1 -right-1 w-7 h-7 rounded-full bg-amber-400 flex items-center justify-center shadow-md">
                  <span className="text-xs text-white font-bold">+</span>
                </button>
              )}
            </div>
            <div className="text-center">
              <h1 className="font-playfair text-xl font-bold text-white">{user?.name}</h1>
              {santriData?.kelas && (
                <span className={`inline-block mt-1 text-xs font-bold px-2.5 py-0.5 rounded-full ${KELAS_CONFIG[santriData.kelas]?.color.replace('text-', 'text-').replace('bg-', 'bg-white/10 text-')}`}>
                  Kelas {santriData.kelas} · {KELAS_CONFIG[santriData.kelas]?.label}
                </span>
              )}
            </div>
          </div>
        </div>

        <div className="px-4 -mt-5 pb-32 md:pb-8 space-y-4">
          {/* Foto upload section */}
          {editFoto && (
            <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}
              className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100">
              <p className="text-xs font-semibold text-gray-600 mb-3">Ganti Foto Profil</p>
              <ImageUpload value={newFoto} onChange={setNewFoto} label="Upload Foto" />
              <div className="flex gap-2 mt-3">
                <button onClick={() => setEditFoto(false)} className="flex-1 py-2.5 rounded-xl text-sm font-semibold text-gray-600 bg-gray-100">Batal</button>
                <button onClick={saveFoto} disabled={!newFoto || saving}
                  className="flex-1 py-2.5 rounded-xl text-sm font-semibold text-white bg-emerald-500 disabled:opacity-50">
                  {saving ? 'Menyimpan...' : 'Simpan'}
                </button>
              </div>
            </motion.div>
          )}

          {/* Stats */}
          <div className="grid grid-cols-2 gap-3 mt-2">
            {stats.map(({ icon: Icon, label, value, color }, i) => (
              <motion.div key={label} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.06 }}
                className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100 text-center">
                <div className={`w-10 h-10 rounded-xl bg-${color}-100 flex items-center justify-center mx-auto mb-2`}>
                  <Icon size={18} className={`text-${color}-600`} />
                </div>
                <p className={`text-2xl font-bold text-${color}-700`}>{value}</p>
                <p className="text-[10px] text-gray-400 font-medium mt-0.5">{label}</p>
              </motion.div>
            ))}
          </div>

          {/* Info Profil */}
          <div className="bg-white rounded-2xl overflow-hidden shadow-sm border border-gray-100">
            <div className="px-4 py-3 border-b border-gray-50">
              <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Data Profil</p>
            </div>
            <div className="divide-y divide-gray-50">
              {infoItems.map(({ label, value }) => (
                <div key={label} className="flex items-center px-4 py-3">
                  <span className="text-xs text-gray-400 w-28 flex-shrink-0">{label}</span>
                  <span className="text-sm font-medium text-gray-800 flex-1">{value}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Rekap Setoran per bulan */}
          {setoran.length > 0 && (
            <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100">
              <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-3">Bergabung Sejak</p>
              <div className="flex items-center gap-2">
                <Calendar size={15} className="text-emerald-500 flex-shrink-0" />
                <span className="text-sm text-gray-700">
                  {setoran[setoran.length - 1]?.tanggal || santriData?.tahun_masuk || '—'}
                </span>
              </div>
            </div>
          )}

          {/* Ganti Password */}
          <div className="bg-white rounded-2xl overflow-hidden shadow-sm border border-gray-100">
            <button
              data-testid="toggle-ganti-password-btn"
              onClick={() => setShowPassForm(s => !s)}
              className="w-full flex items-center justify-between px-4 py-4"
            >
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-xl bg-emerald-100 flex items-center justify-center">
                  <KeyRound size={15} className="text-emerald-600" />
                </div>
                <span className="text-sm font-semibold text-gray-700">Ganti Password</span>
              </div>
              <span className="text-xs text-emerald-500 font-medium">{showPassForm ? 'Tutup' : 'Ubah'}</span>
            </button>
            <AnimatePresence>
              {showPassForm && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  className="overflow-hidden"
                >
                  <form onSubmit={savePassword} data-testid="ganti-password-form" className="px-4 pb-4 pt-0 space-y-3 border-t border-gray-50">
                    {[
                      { field: 'lama', label: 'Password Lama', showKey: 'lama' },
                      { field: 'baru', label: 'Password Baru (min. 6 karakter)', showKey: 'baru' },
                      { field: 'konfirmasi', label: 'Konfirmasi Password Baru', showKey: 'baru' },
                    ].map(({ field, label, showKey }) => (
                      <div key={field} className="pt-2">
                        <label className="block text-xs font-semibold text-gray-500 mb-1">{label}</label>
                        <div className="relative">
                          <input
                            required
                            data-testid={`pass-${field}`}
                            type={showPass[showKey] ? 'text' : 'password'}
                            value={passForm[field]}
                            onChange={e => setPassForm(p => ({ ...p, [field]: e.target.value }))}
                            className="w-full px-3 py-2.5 pr-10 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20"
                          />
                          {showKey !== 'konfirmasi' && (
                            <button type="button"
                              onClick={() => setShowPass(s => ({ ...s, [showKey]: !s[showKey] }))}
                              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">
                              {showPass[showKey] ? <EyeOff size={14} /> : <Eye size={14} />}
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                    <button
                      data-testid="submit-ganti-password-btn"
                      type="submit"
                      disabled={savingPass}
                      className="w-full mt-2 flex items-center justify-center gap-2 bg-emerald-500 hover:bg-emerald-600 text-white py-2.5 rounded-xl text-sm font-semibold disabled:opacity-60"
                    >
                      {savingPass
                        ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        : <><Check size={14} /> Simpan Password</>}
                    </button>
                  </form>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>
    </Layout>
  );
}
